import { useId, useMemo } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  IrisValueObjectsExportFormatEnum,
  ReportCreatePageQuery,
} from 'generated/resources/graphql'

import { getRenderedDefFromTableId } from 'modules/ai_reports/components/DatasourceTypeRenderer/tableRendererRegistry'
import { DateNavigationFilter } from 'modules/ai_reports/components/DateNavigationFilter'
import { Layout } from 'modules/ai_reports/components/Layout'
import { ReportWidget } from 'modules/ai_reports/components/ReportWidget/ReportWidget'
import { SaveModal } from 'modules/ai_reports/components/SaveModal'
import { useColumnOrderTracking } from 'modules/ai_reports/hooks/useColumnOrderTracking'
import { useExportHeaderActions } from 'modules/ai_reports/hooks/useExportHeaderActions'
import { useReportSchema } from 'modules/ai_reports/hooks/useReportSchema'
import { useReportState } from 'modules/ai_reports/hooks/useReportState'
import { assertDefined } from 'modules/ai_reports/lib/assertDefined'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

export const AiReportsMainContent = ({
  sourceId,
  datasource,
}: {
  sourceId: string
  datasource: NonNullable<ReportCreatePageQuery['iris']['factTable']>
}) => {
  assertDefined(sourceId, 'Source ID is required')

  const rendererDef = getRenderedDefFromTableId(sourceId)
  const dateNavigatorConfig = rendererDef?.dateNavigatorConfig?.({})

  const initialDateFilter = useMemo(() => {
    if (dateNavigatorConfig?.defaultValue instanceof Date) {
      return { from: dateNavigatorConfig.defaultValue, to: dateNavigatorConfig.defaultValue }
    }
    return dateNavigatorConfig?.defaultValue
  }, [dateNavigatorConfig?.defaultValue])

  const reportName = 'AI Report'

  const [schema, handleSchemaChanged] = useReportSchema(datasource.columnsConnection.nodes)

  // Use unified state management hook
  const { state, dispatch, agentState, NavConfirmModal, saveReport, handleExport } = useReportState(
    {
      datasourceId: sourceId,
      schema,
      initialDateFilter,
      reportName,
      datasourceName: datasource.name.trim(),
    }
  )

  const { handleColumnStateChange, flushColumnOrder } = useColumnOrderTracking(
    state.columnOrder,
    dispatch
  )

  const defaultSqlQuery = `SELECT * FROM \`${sourceId}\``
  const exportFormats = useMemo<IrisValueObjectsExportFormatEnum[]>(
    () => (datasource.exportFormats.length > 0 ? datasource.exportFormats : ['csv']),
    [datasource.exportFormats]
  )
  useExportHeaderActions({ exportFormats, loading: state.isExporting })

  // Stable widget id (avoids remount of the OneDataCollection subtree on every render,
  // which cancels the in-flight AnalyticsSnapshotResults query and causes a render loop).
  const widgetId = useId()

  return (
    <>
      {/* This is a hack to make the page full height and to use the responsive page body to get the header action clicks*/}
      <Box className="hidden">
        <ResponsivePage.Body
          onActionClick={(key: string) => {
            if (key === 'save') {
              flushColumnOrder()
              dispatch({ type: 'UNSAVED_CHANGES_CONFIRM_REQUESTED' })
            } else {
              const exportFormat = exportFormats.find((format) => key === `export:${format}`)
              if (exportFormat) handleExport(exportFormat)
            }
          }}
          variant="fullscreen"
        >
          &nbsp;
        </ResponsivePage.Body>
      </Box>
      <NavConfirmModal />
      <SaveModal
        open={state.saveModalOpen}
        onClose={() => dispatch({ type: 'UNSAVED_CHANGES_DISMISSED' })}
        onClickSave={saveReport}
        name={agentState?.sqlAnswer?.title}
        description={agentState?.sqlAnswer?.query_summary}
      />
      <Layout
        header={
          dateNavigatorConfig && (
            <DateNavigationFilter
              config={dateNavigatorConfig}
              onSelect={(value) => {
                dispatch({ type: 'DATE_FILTER_UPDATED', payload: value?.value })
              }}
              value={
                state.dateFilter
                  ? {
                      value: {
                        from: state.dateFilter.from,
                        to: state.dateFilter.to ?? state.dateFilter.from,
                      },
                      granularity: dateNavigatorConfig.granularity,
                    }
                  : undefined
              }
            />
          )
        }
      >
        <ReportWidget
          currentFilters={state.datatableFilters ?? undefined}
          sqlQuery={state.sqlQuery}
          description={state.description}
          dateFilter={state.dateFilter}
          tableId={sourceId ?? ''}
          id={widgetId}
          columnFormatHints={agentState?.sqlAnswer?.column_format_hints || []}
          columnOrder={state.columnOrder}
          reportName={reportName}
          hideMetadata={state.sqlQuery === defaultSqlQuery}
          onStateChange={(s) => {
            if (s.filters) {
              dispatch({ type: 'DATACOLLECTION_FILTERS_UPDATED', payload: s.filters })
            }
            handleColumnStateChange(s.settings)
          }}
          schema={schema}
          onSchemaChanged={handleSchemaChanged}
        />
      </Layout>
    </>
  )
}
